import { Badge, Button, Flex, ScrollArea, Text, Title } from '@mantine/core';
import { IconPackage, IconScan, IconTrash } from '@tabler/icons-react';
import { TrackableAssetItem } from './types';

interface ScannedItemsSectionProps {
	items: TrackableAssetItem[];
	onRemoveItem: (itemId: string) => void;
	onScan: () => void;
}

/**
 * Section for scanned bales/items that can be removed
 */
export const ScannedItemsSection: React.FC<ScannedItemsSectionProps> = ({
	items,
	onRemoveItem,
	onScan,
}) => {
	const hasItems = items.length > 0;

	return (
		<Flex direction="column" w="100%" gap="sm">
			<Flex align="center" justify="space-between" px="md" w="100%">
				<Title order={2}>Scanned Items</Title>
				<Badge bg={hasItems ? 'green' : 'red'} size="md" fw={500}>
					{items.length} Items
				</Badge>
			</Flex>

			<Flex px="md">
				{hasItems ? (
					<Flex
						style={{
							border: '1px solid var(--mantine-color-gray-6)',
							borderRadius: '6px',
							maxHeight: '50vh',
							overflow: 'scroll',
						}}
						w="100%"
						direction="column"
					>
						<ScrollArea h="100%" p="md">
							<Flex direction="column" gap="sm">
								{items.map((item, index) => (
									<Flex key={item.id || index} justify="space-between" align="center">
										<Flex direction="column">
											<Text size="sm">{item.scannableCode || `Item ${index + 1}`}</Text>
											<Text size="xs" c="gray">
												{item.intendedMaterial || item.assetType}
											</Text>
										</Flex>
										<Button
											variant="subtle"
											color="red"
											size="xs"
											onClick={() => onRemoveItem(item.id)}
										>
											<IconTrash size={16} />
										</Button>
									</Flex>
								))}
							</Flex>
						</ScrollArea>
					</Flex>
				) : (
					<Flex
						style={{ border: '1px dashed var(--mantine-color-gray-6)', borderRadius: '6px' }}
						w="100%"
						p="md"
						direction="column"
						c="gray"
						justify="center"
						align="center"
						gap="sm"
					>
						<IconPackage size={48} />
						<Text size="md">No items scanned yet</Text>
					</Flex>
				)}
			</Flex>

			<Flex px="md">
				<Button onClick={onScan} fullWidth variant="filled" leftSection={<IconScan size={16} />}>
					Add Scan
				</Button>
			</Flex>
		</Flex>
	);
};
